import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Plus from './components/Plus';
import Structure from './components/Structure';
import Dexter from './components/Dexter';
import AboutApp from './components/AboutApp.jsx';
import ProgramInfo from './components/ProgramInfo.jsx';
import Regulations from './components/Regulations';
import Account from './components/Account';
import Home from './components/Home';
import Profile from './components/Profile';
import Ratings from './components/Ratings';
import Battles from './components/Battles';
import Chats from './components/Chats';
import Login from './components/Login';
import BottomNav from './components/BottomNav';
import Tasks from './components/Tasks';
import { jwtDecode } from "jwt-decode";

//  глобальный контекст роли
import { RoleProvider } from './context/RoleContext';
import { useEmployeeId } from './context/RoleContext';
import { useRoleActions } from './context/RoleContext';
import { useRoleState } from './context/RoleContext';

// используем готовые хелперы токенов
import {
  getAccessToken,
  getRefreshToken,
  isExpired,
  refreshAccessToken,
  logout,
  bootstrapAccessCookie,
  isLogoutInProgress,
} from "./utils/auth";

import { getRangiDeviceId } from './utils/deviceId';
import { logoutIfCurrentDeviceLicenseDenied } from './utils/licenseGuard';

// страница админки
import AdminPanel from './components/AdminPanel';

// 🔌 глобальный сокет для мгновенного логаута при блокировке
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from './config';
import { handleEmployeeTaskStatsSocketUpdate } from './utils/employeeTaskStats';
import { isIosDevice, resetIosPageScroll } from './utils/iosViewport';

function RequireAdmin({ children }) {
  const { role, loading, permissions } = useRoleState();
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#FFFFFF",
          background: "#080808",
          fontSize: 18,
          fontWeight: 500,
          textAlign: "center",
          padding: 24,
          boxSizing: "border-box",
        }}
      >
        Проверяем доступ…
      </div>
    );
  }
  if (role !== 'admin' && !(Array.isArray(permissions) && permissions.includes('users.manage'))) {
    return <Navigate to="/plus" replace />;
  }
  return children;
}

function AppWrapper() {
  return (
    <Router>
      {/* оборачиваем всё приложение провайдером роли */}
      <RoleProvider>
        <App />
      </RoleProvider>
    </Router>
  );
}

function App() {
  const [userId, setUserId] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [tokenTick, setTokenTick] = useState(0); // ← ререндер на любое изменение токенов
  const [authDefer, setAuthDefer] = useState(false); // ← ЯВНЫЙ флаг «идёт попытка авторизации»
  const [authError, setAuthError] = useState(null);  //  fallback вместо белого экрана
  const [sessionUnlocked, setSessionUnlocked] = useState(false); // после первой валидной авторизации не уходим в сплэш

  useEffect(() => {
    const bump = () => setTokenTick(t => t + 1);
    window.addEventListener("auth:tokens-changed", bump);
    // если чистят LS руками (DevTools) — поймаем и это
    window.addEventListener("storage", bump);

    //  Watchdog: заметить смены access в том же табе даже без событий
    let last = getAccessToken();
    const id = window.setInterval(() => {
      const cur = getAccessToken();
      if (cur !== last) {
        last = cur;
        bump();
      }
    }, 500);

    return () => {
      window.removeEventListener("auth:tokens-changed", bump);
      window.removeEventListener("storage", bump);
      window.clearInterval(id);
    };
  }, []);

  const employeeId = useEmployeeId();
  const { refresh: refreshRole } = useRoleActions();
  const location = useLocation();
  const navigate = useNavigate();
  const firstLaunchRef = React.useRef(true);

  const hasDeepLinkNow = React.useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.has('open_chat') || sp.has('open') || sp.has('m') || sp.has('msg') || sp.has('task');
  }, [location.search]);

  const deepLinkSplashMessage = React.useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.has('task')) return 'Открываем задачу…';
    if (sp.has('open_chat') || sp.has('open')) return 'Открываем чат…';
    return null;
  }, [location.search]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onSwMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'NAVIGATE' || !data.path) return;
      navigate(String(data.path));
    };

    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, [navigate]);

  async function tryRefreshWithRetry() {
    if (isLogoutInProgress()) return false;
    try {
      await refreshAccessToken();
      setAuthError(null);
      return true;
    } catch {
      try {
        await new Promise(r => setTimeout(r, 700));
        if (isLogoutInProgress()) return false;
        await refreshAccessToken();
        setAuthError(null);
        return true;
      } catch (e2) {
        setAuthError("refresh_failed");
        return false;
      }
    }
  }

  function SplashScreen({ message = "Открываем чат…", showRetry = false }) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          padding: 24,
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 360, width: "100%" }}>
          <div style={{ fontSize: 16, lineHeight: 1.4, marginBottom: showRetry ? 16 : 0 }}>
            {message}
          </div>

          {showRetry ? (
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid #d0d0d0",
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                Повторить
              </button>
              <button
                onClick={() => logout("refresh_expired")}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid #d0d0d0",
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                Войти заново
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ===== bootstrap cookie для старых сессий (1 раз за вкладку) =====
  useEffect(() => {
    if (location.pathname === '/login') return;

    (async () => {
      // best-effort: не блокируем интерфейс
      try {
        await bootstrapAccessCookie();
      } catch {
        // ignore
      }

      // ✅ Fallback: если refresh не сработал/нет refresh, но есть валидный access в LS,
      // попросим бэк выставить HttpOnly access_token cookie по Bearer.
      try {
        if (sessionStorage.getItem("bearer_cookie_bootstrap_done") === "1") return;

        const access = getAccessToken();
        if (!access || isExpired(access)) {
          sessionStorage.setItem("bearer_cookie_bootstrap_done", "1");
          return;
        }

        await fetch(`${API_URL}/auth/bootstrap-cookie`, {
          method: "POST",
          headers: { Authorization: `Bearer ${access}` },
          credentials: "include", // важно, чтобы браузер принял Set-Cookie
        });

        sessionStorage.setItem("bearer_cookie_bootstrap_done", "1");
      } catch {
        try { sessionStorage.setItem("bearer_cookie_bootstrap_done", "1"); } catch { }
      }
    })();

  }, [location.pathname]);

  // ===== ТИХИЙ REFRESH при маунте и при смене маршрута =====
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ⚠️ если уже на /login — не инициируем выход/редирект
        if (location.pathname === '/login') return;
        if (isLogoutInProgress()) return;

        const access = getAccessToken();

        // Свежий access — ничего не делаем
        if (access && !isExpired(access)) {
          setAuthError(null);
          return;
        }

        // Access отсутствует или истёк — пробуем рефреш по HttpOnly cookie
        await tryRefreshWithRetry();
      } finally {
        if (!cancelled) setAuthReady(true); // разблокируем рендер маршрутов
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // ===== Планировщик рефреша за ~10 сек до истечения access =====
  useEffect(() => {
    if (isLogoutInProgress()) return;
    const access = getAccessToken();
    if (!access) return;

    let timeoutId;
    try {
      const { exp } = jwtDecode(access) || {};
      if (exp && typeof exp === 'number') {
        const msToExp = exp * 1000 - Date.now();
        const fireIn = Math.max(msToExp - 10_000, 1_000); // сработать за 10с до истечения (минимум через секунду)
        timeoutId = window.setTimeout(async () => {
          if (isLogoutInProgress()) return;
          const a = getAccessToken();
          if (a && isExpired(a)) {
            try {
              await refreshAccessToken(); // возьмёт refresh из HttpOnly cookie
              setAuthError(null);
            } catch {
              // не дёргаем пользователя мгновенным logout — дадим controlled fallback
              setAuthError("refresh_failed");
            }
          }
        }, fireIn);
      }
    } catch {
      // игнорируем проблемы декодирования
    }
    return () => { if (timeoutId) clearTimeout(timeoutId); };
    // перезапуск при смене маршрута и каждом обновлении access-токена (см. ниже вычисление token)
  }, [location.pathname, getAccessToken()]);

  // ===== Подстраховка: обновляемся при возврате на вкладку =====
  useEffect(() => {
    const onVis = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!authReady) return;
      if (isLogoutInProgress()) return;

      if (isIosDevice()) {
        resetIosPageScroll();
        requestAnimationFrame(resetIosPageScroll);
      }

      const access = getAccessToken();
      if (access && !isExpired(access)) return;

      // На iOS/PWA агрессивный logout здесь даёт дёргания при открытии из push.
      // Делаем best-effort refresh без мгновенного вылета на логин.
      try {
        await refreshAccessToken(); // cookie-based
        setAuthError(null);
      } catch {
        setAuthError(prev => prev || "refresh_failed");
      }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [authReady]);

  // После готовности авторизации — освежить роль
  useEffect(() => {
    if (authReady) {
      // не бросаем ошибку наружу, чтобы не ломать рендер
      Promise.resolve(refreshRole()).catch(() => { });
    }
  }, [authReady, refreshRole]);

  // Текущий токен и признак авторизации — считаем только после silent refresh
  // tokenTick заставит этот блок обновляться при любом изменении токенов
  const access = getAccessToken();
  const hasValidAccess = !!access && !isExpired(access);
  const isAuthenticated = authReady && hasValidAccess;
  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname === '/login') return;

    let cancelled = false;

    (async () => {
      const loggedOut = await logoutIfCurrentDeviceLicenseDenied();

      if (cancelled || !loggedOut) return;

      navigate('/login', { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    if (hasValidAccess) setSessionUnlocked(true);
  }, [hasValidAccess]);

  useEffect(() => {
    if (location.pathname === '/login') {
      setSessionUnlocked(false);
    }
  }, [location.pathname]);

  // 🔌 Глобальный сокет: выкинуть пользователя при блокировке/удалении + логи/ack
  useEffect(() => {
    if (!authReady) return;

    const access = getAccessToken();
    if (!access || isExpired(access)) return;

    const s = io(SOCKET_URL, {
      path: '/ws/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      auth: (cb) => {
        cb({
          token: getAccessToken(),
          device_id: getRangiDeviceId(),
        });
      },
    });

    // логи подключения/ошибок
    s.on('connect', () => {
      console.log('socket подключен по токену');
    });
    s.on('connect_error', async (err) => {
      console.log('connect_error:', err?.message || err);

      const loggedOut = await logoutIfCurrentDeviceLicenseDenied();

      if (loggedOut) {
        try {
          s.io.opts.reconnection = false;
          s.disconnect();
        } catch {}

        navigate('/login', { replace: true });
      }
    });

    s.on('user_blocked', logout);
    s.on('user_deleted', logout);
    s.on('employee_task_stats:update', handleEmployeeTaskStatsSocketUpdate);

    return () => s.disconnect();
  }, [authReady, tokenTick]);

  // ♻️ Если access пропал/протух уже после инициализации — пробуем поднять сессию по cookie
  useEffect(() => {
    if (!authReady) return;
    if (location.pathname === '/login') return;
    if (isLogoutInProgress()) return;

    const a = getAccessToken();
    if (a && !isExpired(a)) return;

    let cancelled = false;
    (async () => {
      setAuthDefer(true);          // ← включаем «мост»: НЕ выключаем текущий экран
      try {
        const ok = await tryRefreshWithRetry();
        if (!ok && !cancelled) {
          setAuthError("refresh_failed");
        }
      } finally {
        if (!cancelled) {
          setAuthDefer(false);     // ← выключаем «мост»
        }
      }
    })();

    return () => { cancelled = true; };
  }, [authReady, location.pathname, getAccessToken(), tokenTick]);

  // ️ Одноразовый редирект на Главную после инициализации авторизации
  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    if (!firstLaunchRef.current) return;

    //  Не мешаем диплинкам из пушей и внутренних ссылок
    const sp = new URLSearchParams(location.search);
    const hasDeepLink =
      sp.has('open_chat') || sp.has('open') || sp.has('m') || sp.has('msg') || sp.has('task');

    // Если есть диплинк — считаем «первый запуск» обработанным и ничего не делаем
    if (hasDeepLink) {
      firstLaunchRef.current = false;
      return;
    }

    firstLaunchRef.current = false;

    // сохраняем query/hash при редиректе на /plus (как и было)
    navigate(
      { pathname: "/plus", search: location.search, hash: location.hash },
      { replace: true }
    );
  }, [authReady, isAuthenticated, location.search, location.hash, navigate]);

  // userId берём синхронно из access-токена (нужно для ссылок/чатов)
  useEffect(() => {
    if (!access) {
      setUserId(null);
      return;
    }
    try {
      const decoded = jwtDecode(access);
      setUserId(decoded?.sub ?? null);
    } catch (err) {
      console.error("Ошибка декодирования токена", err);
      setUserId(null);
    }
  }, [access, location.pathname]);

  const isLoginPage = location.pathname === "/login";

  // ❗ Маршруты, на которых верхнего меню быть не должно
  const HIDE_NAV_PATHS = ['/plus', '/change-password', '/news', '/chats', '/admin'];
  const isNavHiddenRoute = HIDE_NAV_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  const shouldShowTopNav = !isLoginPage && !isNavHiddenRoute;

  const HIDE_SIDEBAR_PATHS = ['/structure', '/login'];
  const isSidebarHiddenRoute = HIDE_SIDEBAR_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Пока идёт первичный «тихий» refresh — показываем сплэш вместо белого экрана
  if (!authReady) {
    return <SplashScreen message={deepLinkSplashMessage || (hasDeepLinkNow ? "Открываем чат…" : "Загружаем приложение…")} />;
  }

  // ✅ Новый единый признак: можно ли рендерить приватные страницы.
  //    Разрешаем рендер, если токен валиден ИЛИ идёт мост (authDefer).
  const canRenderPrivate = authReady && (hasValidAccess || authDefer || sessionUnlocked);

  const privateFallback = authError
    ? <SplashScreen message="Не удалось восстановить сессию" showRetry />
    : <SplashScreen message={deepLinkSplashMessage || (hasDeepLinkNow ? "Открываем чат…" : "Загружаем данные…")} />;

  return (
    <div className="App">
      <main className={isLoginPage ? "login-main" : undefined}>
        <Routes>
          {/* дефолтный редирект на Главную */}
          <Route path="/" element={<Navigate to="/plus" replace />} />

          {/* приватные страницы: НЕ блокируем на время authDefer */}
          <Route
            path="/news"
            element={
              !canRenderPrivate
                ? privateFallback
                : <Home />
            }
          />
          <Route
            path="/plus"
            element={
              !canRenderPrivate
                ? privateFallback
                : <Plus />
            }
          />
          <Route
            path="/about"
            element={
              !canRenderPrivate
                ? privateFallback
                : <AboutApp />
            }
          />
          <Route
            path="/program"
            element={
              !canRenderPrivate
                ? privateFallback
                : <ProgramInfo />
            }
          />
          <Route
            path="/regulations"
            element={
              !canRenderPrivate
                ? privateFallback
                : <Regulations />
            }
          />

          <Route path="/account/me" element={<Account />} />
          <Route path="/account/:employeeId" element={<Account />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/ratings" element={<Ratings />} />
          <Route path="/battles" element={<Battles />} />
          <Route path="/tasks" element={!canRenderPrivate ? privateFallback : <Tasks />} />
          <Route
            path="/structure"
            element={!canRenderPrivate ? privateFallback : <Structure />}
          />

          <Route
            path="/dexter"
            element={!canRenderPrivate ? privateFallback : <Dexter />}
          />

          <Route
            path="/dexter/folder/:folderId"
            element={!canRenderPrivate ? privateFallback : <Dexter />}
          />

          <Route
            path="/chats"
            element={
              !canRenderPrivate
                ? privateFallback
                : <Chats userId={userId} />
            }
          />

          {/* если уже авторизован — не показываем форму логина, а уводим на /plus */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/plus" replace /> : <Login />}
          />

          <Route
            path="/admin"
            element={
              !canRenderPrivate
                ? privateFallback
                : (
                  <RequireAdmin>
                    <AdminPanel />
                  </RequireAdmin>
                )
            }
          />
        </Routes>
      </main>
      {!isSidebarHiddenRoute && <BottomNav />}
    </div>
  );
}

export default AppWrapper;